import { useEffect, useState } from "react";

/**
 * Track the on-screen keyboard (iOS standalone). When it opens, the visual
 * viewport shrinks while the layout viewport stays put — returning {height,
 * top} lets a full-height screen pin itself to the visible area: header stays,
 * the composer rides the keyboard, only the inner thread scrolls (iMessage
 * behavior).
 *
 * iOS standalone quirks handled here:
 * - resize/scroll events around the keyboard are unreliable → besides
 *   listening, poll hard (every frame) for ~1.5s after any focus change, and
 *   keep a slow safety-net interval running the whole time the keyboard is up
 *   so a missed event can never strand the composer behind the keyboard.
 * - iOS "helpfully" pans the page to reveal the focused input (that's the
 *   jump). The pan can land in EITHER window.scrollY or vv.offsetTop
 *   depending on mode, so compensate with the sum of both (== vv.pageTop).
 */
export function useVisualViewport(): { height?: number; top?: number } {
  const [box, setBox] = useState<{ height?: number; top?: number }>({});

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    let raf = 0;
    let keyboardUp = false;

    const measure = () => {
      // Keyboard overlap = how much shorter the visual viewport is than the layout.
      const overlap = Math.max(0, window.innerHeight - vv.height);
      if (overlap > 60) {
        keyboardUp = true;
        const height = Math.round(vv.height);
        // Document offset of the visible area's top edge — covers both the
        // "layout viewport scrolled" and "visual viewport panned" cases.
        const top = Math.max(0, Math.round(vv.offsetTop + window.scrollY));
        setBox((b) => (b.height === height && b.top === top ? b : { height, top }));
      } else {
        keyboardUp = false;
        setBox((b) => (b.height === undefined ? b : {}));
        if (window.scrollY !== 0) window.scrollTo(0, 0);
      }
    };

    // Poll through the keyboard's show/hide animation (~1.5s of frames).
    const poll = () => {
      cancelAnimationFrame(raf);
      let frames = 0;
      const tick = () => {
        measure();
        if (++frames < 90) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    };

    // Safety net: while the keyboard is up, re-measure a few times a second
    // even if iOS never fires an event (QuickType bar growing/shrinking,
    // standalone-mode missed resizes, etc.).
    const interval = window.setInterval(() => { if (keyboardUp) measure(); }, 250);

    vv.addEventListener("resize", measure);
    vv.addEventListener("scroll", measure);
    window.addEventListener("focusin", poll);
    window.addEventListener("focusout", poll);
    measure();
    return () => {
      vv.removeEventListener("resize", measure);
      vv.removeEventListener("scroll", measure);
      window.removeEventListener("focusin", poll);
      window.removeEventListener("focusout", poll);
      cancelAnimationFrame(raf);
      clearInterval(interval);
    };
  }, []);

  return box;
}
